import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import { isEqual } from "lodash";
import { useNavigate } from "react-router-dom";
import useSWR from "swr";

import { getJournalByWorkspaceID, setJournalArchieve } from "../../Api/Services/JournalServices.js";
import {
  getPetInfoList,
  getPetNamesByWorkspaceId,
} from "../../Api/Services/PetServices.js";
import "./GlobalItemList.css";
import { ReactComponent as ViewIcon } from "../../Assets/Images/viewIcon.svg";
import useAuth from "../../ContextProvider/AuthProvider/AuthProvider.js";
import { filterAscDescData, formatDate, hexToRgba } from "../../Utils.js";
import DetailsModal from "../Common/DetailsModal.js";
import PawItemList from "../Common/PawItemList.js";
import { Button } from "@mui/material";
import { useTheme } from "@mui/material/styles";

export default function Journal() {
  const { workspaceId: w_id } = useAuth();
  const [selectedPetName, setSelectedPetName] = useState("");
  const [open, setOpen] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [archiveId, setArchiveId] = useState(null);
  const [filter, setFilter] = useState({});
  const [pageCount, setPageCount] = useState(0);
  const [journalData, setJournalData] = useState([]);
  const fetchIdRef = useRef(0);
  const navigate = useNavigate();
  const theme = useTheme();

  const handleArchiveClick = (surgery_id) => {
    setShowSuccessMessage(true);
    setOpen(true);
    setArchiveId(surgery_id); 
  };
  const handleArchive = () => {
    setIsUpdating(true);

    setJournalArchieve(archiveId)
      .then((response) => {
        setIsUpdating(false);
        console.log(response);
        if (response.data.success === true) {
          window.location.reload();
        }
      })
      .catch((err) => {
        console.error(err);
        setOpen(true);
        setIsUpdating(false);
      });
  };

  const { data, isLoading } = useSWR(
    w_id ? `data${w_id}` : null,
    async () => {
      const { data } = await getJournalByWorkspaceID(w_id);
      return data?.data || [];
    },
    {
      revalidateOnFocus: false,
    }
  );
  const fetchData = useCallback((oFilter) => {
  const fetchId = ++fetchIdRef.current;
    if (fetchId === fetchIdRef.current && !isEqual(filter, oFilter)) {
      setFilter(oFilter);
    }
  }, []);

  useEffect(() => {
    const { limit, from, sort } = filter;
    if (filter && filter.from > -1) {
      if (data?.length && limit) {
        setPageCount(Math.ceil((data?.length) / limit));
        const allData = [...data];
        const filteredResult = filterAscDescData(allData, sort, from, limit);
        setJournalData(filteredResult);
      }
    }
  }, [filter, data]);

  const { data: petData } = useSWR(
    `petData`,
    async () => {
      const { data } = await getPetInfoList();
      return data?.data || [];
    },
    {
      revalidateOnFocus: false,
    }
  );

  const { data: petNames = [] } = useSWR(
    w_id ? `petNames${w_id}` : null,
    async () => {
      const { data } = await getPetNamesByWorkspaceId(w_id);
      return data?.data || [];
    },
    {
      revalidateOnFocus: false,
    }
  );

  const result = journalData
    ? journalData?.map((journal) => {
        const { journal_id, date, note, pet_id } = journal;
        const matchingPet = petData?.find((pet) => pet.id === pet_id);

        const formattedDate = date
          ? formatDate(date)
          : null;

      return {
          id: journal_id,
          journal_id,
          date: formattedDate,
          note,
          petid: pet_id,
          name: matchingPet ? matchingPet.name : "",
          image: matchingPet ? matchingPet.image : "",
        };
      })
    : [];

  let filteredData;

  if (selectedPetName === "") {
    filteredData = result;
  } else if (!result) {
    filteredData = [];
  } else {
    filteredData = result.filter((item) => {
      const petName = item.name.toLowerCase() || "";
      const query = selectedPetName.toLowerCase();
      return petName.includes(query);
    });
  }

  const petNamesSet = new Set();
  const filteredPetNames = petNames?.filter((pet) => {
    const lowercaseName = pet.name.toLowerCase();
    if (!petNamesSet.has(lowercaseName)) {
      petNamesSet.add(lowercaseName);
      return true;
    }
    return false;
  });

  const defaultOptions = {
    pageSize: 5,
  };

  const columns = useMemo(() => {
    return [
      {
        Header: "",
        accessor: "image",
        width: "8vw",
        disableSortBy: true,
        Cell: ({ row }) => {
          return row?.original?.image ? (
            <img
              alt="petImage"
              src={row?.original?.image}
              style={{
                width: "50px",
                height: "50px",
                borderRadius: "10px",
                objectFit: "cover",
                margin: "0 auto",
                verticalAlign: "middle",
              }}
            />
          ) : (
            <AccountCircleIcon
              sx={{
                width: "50px",
                height: "50px",
              }}
            />
          );
        },
      },
      {
        Header: "Pet Name",
        accessor: "name",
        width: "12.5vw",
      },
      {
        Header: `Date`,
        accessor: "date",
        width: "12.5vw",
      },
      {
        Header: "Note",
        accessor: "note",
        width: "12.5vw",
      },
      {
        Header: "",
        accessor: "journal_id",
        width: "8vw",
        isArchive: true,
        disableSortBy: true,
        disabled: isUpdating,
        textAlign: "center",
        Cell: ({ row }) => {
          return (
            <UnarchiveIcon
              style={{
                color: "#000000",
              }}
              onClick={() =>
                handleArchiveClick(row?.original?.journal_id)
              }
            />
          );
        },
      },
      {
        Header: "Action",
        accessor: "action",
        width: "8vw",
        disableSortBy: true,
        textAlign: "center",
        Cell: ({ row }) => {
          return (
            <Button
              sx={{
                minWidth: "40px",
                background: `${hexToRgba(theme.palette.primary.main, 0.20)} !important`,
                borderRadius: "10px",
                marginRight: "5px",
                width: "24px",
              }}
              onClick={() => navigate(props.viewDataLink(row.original))}
            >
             <ViewIcon
                style={{
                  fill: `${theme.palette.primary.main}`, 
                  cursor: "pointer",
                }}
                alt="view"
              />
            </Button>
          );
        },
      },
    ];
  }, []);

  const props = {
    label: "Journal",
    setSelectedUserName: setSelectedPetName,
    items: filteredPetNames,
    isLoading: isLoading,
    dataAddLink: "/journal/form",
    columns,
    columnsData: filteredData,
    viewDataLink: (data) =>
    `/journal/info/${data.journal_id}`,
    placeholder: "pet name",
    details: "There are no pet notes for this user.",
    fetchData,
    pageCount,
    defaultOptions,
  };

  const ButtonItem = [
    {
      name: "yes",
      onClick: () => {
        handleArchive();
        setShowSuccessMessage(false);
      },
    },
    {
      name: "no",
      onClick: () => {
        setOpen(false);
        setShowSuccessMessage(false);
      },
    },
  ];

  return (
    <>
      <PawItemList {...props} />
      {showSuccessMessage && (
        <>
          <DetailsModal
            open={open}
            severity="warning"
            buttonList={ButtonItem}
            title="Do you want to archive this journal?"
            handleClose={()=> setOpen(false)}
          />
        </>
      )}
    </>
  );
}
